import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Package } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

/**
 * Toggle card for enabling/disabling OptiAssets addon
 * Manager-only, shown in Shop Profile settings
 */
export function OptiAssetsToggle() {
  const { activeShop, refreshActiveShop } = useAuth();
  const [isUpdating, setIsUpdating] = useState(false);
  const [localEnabled, setLocalEnabled] = useState<boolean | null>(null);
  
  const isEnabled = localEnabled ?? (activeShop?.addons?.includes('OPTIASSETS') ?? false);

  const handleToggle = async (enabled: boolean) => {
    if (!activeShop?.id) return;
    
    setIsUpdating(true);
    try {
      const { data, error } = await supabase.functions.invoke('update-shop-addons', {
        body: { shopId: activeShop.id, addon: 'OPTIASSETS', enabled },
      });
      
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      
      setLocalEnabled(enabled);
      await refreshActiveShop();
      toast.success(enabled ? 'OptiAssets enabled' : 'OptiAssets disabled');
    } catch (error: any) {
      toast.error('Failed to update', { description: error.message });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Package className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                OptiAssets
                <Badge variant="outline" className="text-xs">Beta</Badge>
              </CardTitle>
              <CardDescription>Equipment tracking & analytics</CardDescription>
            </div>
          </div>
          <Switch
            checked={isEnabled}
            onCheckedChange={handleToggle}
            disabled={isUpdating}
          />
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <p className="text-sm text-muted-foreground">
          Track equipment usage, maintenance schedules, and see how your assets enable labor capacity.
        </p>
      </CardContent>
    </Card>
  );
}
